import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ChevronLeft, Save, RotateCcw, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import ImageUploader from "@/components/admin/ImageUploader";
import VariantEditor from "@/components/admin/VariantEditor";
import { toast } from "sonner";

const BLANK = {
    name: "",
    slug: "",
    short_description: "",
    description: "",
    category_id: "",
    brand_id: "",
    product_type: "simple",
    price: 0,
    sale_price: null,
    stock: 0,
    sku: "",
    images: [],
    primary_image: "",
    attributes_schema: [],
    variants: [],
    featured: false,
    new_arrival: true,
    on_sale: false,
    active: true,
    seo_title: "",
    seo_description: "",
    // Shipping (Phase 1)
    weight_grams: "",
    length_cm: "",
    width_cm: "",
    height_cm: "",
    shipping_type: "standard",
    free_shipping_eligible: false,
};

// ---------------------------------------------------------------------------
// Draft autosave — persists form state to localStorage so a session timeout,
// tab refresh, or stray click doesn't wipe a half-typed product. Stored
// fields are plain strings/numbers/booleans/arrays already; image URLs are
// strings, so they round-trip safely. Raw upload File objects are NEVER
// stored here.
// ---------------------------------------------------------------------------
const draftKey = (id, isNew) => `admin_product_draft_${isNew ? "new" : id}`;
const DRAFT_DEBOUNCE_MS = 800;

function readDraft(key) {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return null;
        return parsed;
    } catch (_) {
        return null;
    }
}

export default function ProductForm() {
    const { id } = useParams();
    const isNew = !id || id === "new";
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [form, setForm] = useState(BLANK);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    // Draft autosave state
    const dkey = draftKey(id, isNew);
    const [pendingDraft, setPendingDraft] = useState(null); // null = no banner, object = offer restore
    const draftEnabledRef = useRef(false); // gate writes until initial server-load completes
    const autosaveTimerRef = useRef(null);

    useEffect(() => {
        api.get("/admin/categories").then((r) => setCategories(r.data || []));
        api.get("/admin/brands").then((r) => setBrands(r.data || []));
        // Detect a pending draft BEFORE loading server data so we can offer
        // restore without surprise-overwriting fresh server values.
        const draft = readDraft(dkey);
        if (isNew) {
            if (draft) setPendingDraft(draft);
            // For new products there's no server payload to fetch; enable autosave immediately.
            draftEnabledRef.current = true;
            return;
        }
        api.get(`/admin/products/${id}`).then((r) => {
            setForm({ ...BLANK, ...r.data });
            // After server hydration, enable autosave writes.
            draftEnabledRef.current = true;
            if (draft) setPendingDraft(draft);
        }).catch(() => {
            toast.error("Product not found");
            navigate("/admin/products");
        });
    }, [id, isNew, navigate, dkey]);

    // Debounced autosave — fires only after the form has been hydrated.
    useEffect(() => {
        if (!draftEnabledRef.current) return;
        if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = setTimeout(() => {
            try {
                localStorage.setItem(dkey, JSON.stringify({ ...form, _saved_at: Date.now() }));
            } catch (_) {
                // Quota errors etc. — silently ignore; user just loses the cushion.
            }
        }, DRAFT_DEBOUNCE_MS);
        return () => {
            if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
        };
    }, [form, dkey]);

    const restoreDraft = () => {
        if (!pendingDraft) return;
        // Strip our internal timestamp before merging.
        const { _saved_at, ...rest } = pendingDraft;
        setForm((f) => ({ ...f, ...rest }));
        setPendingDraft(null);
        toast.success("Draft restored");
    };

    const discardDraft = () => {
        try { localStorage.removeItem(dkey); } catch (_) {}
        setPendingDraft(null);
    };

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.name?.trim()) {
            setError("Product name is required");
            return;
        }
        // Strip ids beginning with local- (server will assign UUIDs)
        const cleanedVariants = (form.variants || []).map((v) => {
            const next = { ...v };
            if (next.id?.startsWith("local-")) delete next.id;
            // Coerce nullable sale_price
            if (next.sale_price === "" || next.sale_price === undefined) next.sale_price = null;
            return next;
        });
        const payload = {
            ...form,
            variants: form.product_type === "variant" ? cleanedVariants : [],
            attributes_schema: form.product_type === "variant" ? form.attributes_schema : [],
            sale_price:
                form.sale_price === "" || form.sale_price === undefined || form.sale_price === null
                    ? null
                    : Number(form.sale_price),
            price: Number(form.price || 0),
            stock: Number(form.stock || 0),
            category_id: form.category_id || null,
            brand_id: form.brand_id || null,
            // Shipping fields — coerce empty strings to null
            weight_grams: form.weight_grams === "" || form.weight_grams === null ? null : Number(form.weight_grams),
            length_cm: form.length_cm === "" || form.length_cm === null ? null : Number(form.length_cm),
            width_cm: form.width_cm === "" || form.width_cm === null ? null : Number(form.width_cm),
            height_cm: form.height_cm === "" || form.height_cm === null ? null : Number(form.height_cm),
            shipping_type: form.shipping_type || "standard",
            free_shipping_eligible: !!form.free_shipping_eligible,
        };

        setSubmitting(true);
        try {
            if (isNew) {
                const { data } = await api.post("/admin/products", payload);
                try { localStorage.removeItem(dkey); } catch (_) {}
                toast.success("Product created");
                navigate(`/admin/products/${data.id}`);
            } else {
                await api.put(`/admin/products/${id}`, payload);
                try { localStorage.removeItem(dkey); } catch (_) {}
                toast.success("Product updated");
            }
        } catch (e) {
            // Session expired — keep the draft + form intact, ask user to relogin.
            if (e.response?.status === 401) {
                setError("Your admin session expired. Please login again in a new tab. Your product draft is safe.");
                toast.error("Session expired. Your product draft is safe.", {
                    description: "Open /admin/login in a new tab, then come back and click Save.",
                    duration: 8000,
                });
            } else {
                setError(formatApiError(e.response?.data?.detail) || e.message);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-5" data-testid="admin-product-form">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <Link to="/admin/products" className="text-xs uppercase tracking-widest text-white/55 hover:text-white inline-flex items-center gap-1">
                        <ChevronLeft size={14} /> All products
                    </Link>
                    <h1 className="font-display text-3xl font-black uppercase tracking-tight mt-1">
                        {isNew ? "New product" : form.name || "Edit product"}
                    </h1>
                </div>
                <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest px-5 py-3 rounded-sm hover:bg-[#33C9EA] disabled:opacity-50"
                    data-testid="admin-product-save"
                >
                    <Save size={16} /> {submitting ? "Saving…" : "Save"}
                </button>
            </div>

            {pendingDraft && (
                <div
                    className="bg-[#00BCE5]/10 border border-[#00BCE5]/40 text-[#9BE7F7] rounded-sm px-4 py-3 text-sm flex items-center justify-between gap-3 flex-wrap"
                    data-testid="product-draft-banner"
                >
                    <div>
                        <strong className="text-[#00BCE5]">Unsaved product draft found.</strong>{" "}
                        Restore your previous edits?
                        {pendingDraft._saved_at && (
                            <span className="ml-2 text-white/45 text-xs">
                                (saved {new Date(pendingDraft._saved_at).toLocaleString()})
                            </span>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={restoreDraft}
                            className="inline-flex items-center gap-1.5 bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest text-xs px-3 py-2 rounded-sm hover:bg-[#33C9EA]"
                            data-testid="product-draft-restore"
                        >
                            <RotateCcw size={13} /> Restore
                        </button>
                        <button
                            type="button"
                            onClick={discardDraft}
                            className="inline-flex items-center gap-1.5 border border-white/30 text-white/85 font-display font-bold uppercase tracking-widest text-xs px-3 py-2 rounded-sm hover:border-white/55"
                            data-testid="product-draft-discard"
                        >
                            <X size={13} /> Discard
                        </button>
                    </div>
                </div>
            )}

            {error && <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF6E66] rounded-sm px-4 py-3 text-sm" data-testid="form-error">{error}</div>}

            <div className="grid lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 space-y-5">
                    <Card title="Basics">
                        <Field label="Name *">
                            <input value={form.name} onChange={(e) => set("name", e.target.value)} className="ost-input" data-testid="pf-name" />
                        </Field>
                        <Field label="Slug (auto from name)">
                            <input value={form.slug} onChange={(e) => set("slug", e.target.value)} className="ost-input" placeholder="auto" data-testid="pf-slug" />
                        </Field>
                        <Field label="Short description">
                            <input value={form.short_description} onChange={(e) => set("short_description", e.target.value)} className="ost-input" data-testid="pf-short-desc" />
                        </Field>
                        <Field label="Full description">
                            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="ost-input min-h-[140px]" data-testid="pf-desc" />
                        </Field>
                    </Card>

                    <Card title="Product type & pricing">
                        <div className="flex gap-3">
                            {["simple", "variant"].map((t) => (
                                <button
                                    type="button"
                                    key={t}
                                    onClick={() => set("product_type", t)}
                                    className={`flex-1 py-3 rounded-sm border font-display uppercase tracking-widest font-bold ${
                                        form.product_type === t ? "border-[#00BCE5] text-[#00BCE5] bg-[#00BCE5]/10" : "border-[#27272A] text-white/65"
                                    }`}
                                    data-testid={`pf-type-${t}`}
                                >
                                    {t === "simple" ? "Simple" : "Variants"}
                                </button>
                            ))}
                        </div>

                        {form.product_type === "simple" ? (
                            <div className="grid md:grid-cols-3 gap-4">
                                <Field label="Price">
                                    <input
                                        type="number"
                                        value={form.price}
                                        onChange={(e) => set("price", Number(e.target.value))}
                                        className="ost-input"
                                        data-testid="pf-price"
                                    />
                                </Field>
                                <Field label="Sale price (optional)">
                                    <input
                                        type="number"
                                        value={form.sale_price ?? ""}
                                        onChange={(e) => set("sale_price", e.target.value === "" ? null : Number(e.target.value))}
                                        className="ost-input"
                                        data-testid="pf-sale-price"
                                    />
                                </Field>
                                <Field label="Stock">
                                    <input
                                        type="number"
                                        value={form.stock}
                                        onChange={(e) => set("stock", Number(e.target.value))}
                                        className="ost-input"
                                        data-testid="pf-stock"
                                    />
                                </Field>
                                <Field label="SKU">
                                    <input value={form.sku} onChange={(e) => set("sku", e.target.value)} className="ost-input" data-testid="pf-sku" />
                                </Field>
                            </div>
                        ) : (
                            <VariantEditor
                                attributesSchema={form.attributes_schema || []}
                                setAttributesSchema={(v) => set("attributes_schema", v)}
                                variants={form.variants || []}
                                setVariants={(v) => set("variants", v)}
                                basePrice={form.price}
                            />
                        )}
                    </Card>

                    <Card title="Images">
                        <ImageUploader
                            images={form.images || []}
                            primary={form.primary_image}
                            onChange={(arr) => set("images", arr)}
                            onPrimaryChange={(url) => set("primary_image", url)}
                        />
                    </Card>
                </div>

                <div className="space-y-5">
                    <Card title="Organize">
                        <Field label="Category">
                            <select
                                value={form.category_id || ""}
                                onChange={(e) => set("category_id", e.target.value)}
                                className="ost-input"
                                data-testid="pf-category"
                            >
                                <option value="">—</option>
                                {categories.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Brand">
                            <select
                                value={form.brand_id || ""}
                                onChange={(e) => set("brand_id", e.target.value)}
                                className="ost-input"
                                data-testid="pf-brand"
                            >
                                <option value="">—</option>
                                {brands.map((b) => (
                                    <option key={b.id} value={b.id}>{b.name}</option>
                                ))}
                            </select>
                        </Field>
                    </Card>
                    <Card title="Visibility">
                        <Toggle label="Active" value={form.active} onChange={(v) => set("active", v)} testid="pf-active" />
                        <Toggle label="Featured" value={form.featured} onChange={(v) => set("featured", v)} testid="pf-featured" />
                        <Toggle label="New arrival" value={form.new_arrival} onChange={(v) => set("new_arrival", v)} testid="pf-new" />
                        <Toggle label="On sale" value={form.on_sale} onChange={(v) => set("on_sale", v)} testid="pf-onsale" />
                    </Card>
                    <Card title="Shipping & Package">
                        {/* SHIPPING DATA QUALITY — three rules:
                            1) Active + no weight + not a fully-weighted variant product → RED warning (existing products) or hard-block message (new)
                            2) Active + no dimensions → AMBER tip
                            3) Inactive / draft → no nag
                        */}
                        {(() => {
                            if (!form.active) return null;
                            const w = Number(form.weight_grams || 0);
                            const variantsAllHaveWeight = (form.product_type === "variant")
                                && (form.variants || []).filter(v => v.active !== false).length > 0
                                && (form.variants || []).filter(v => v.active !== false).every(v => Number(v.shipping_weight_g || 0) > 0);
                            const weightMissing = w <= 0 && !variantsAllHaveWeight;
                            const dimsSet = ["length_cm", "width_cm", "height_cm"].some(k => Number(form[k] || 0) > 0);
                            return (
                                <>
                                    {weightMissing && (
                                        <div
                                            data-testid="pf-shipping-warning-weight"
                                            className={`text-xs px-2.5 py-2 rounded-sm border ${
                                                isNew
                                                    ? "text-red-300 bg-red-500/10 border-red-500/40"
                                                    : "text-amber-400 bg-amber-500/10 border-amber-500/30"
                                            }`}
                                        >
                                            {isNew ? (
                                                <>
                                                    <strong>Required:</strong> set <span className="font-mono">weight_grams</span> (or per-variant <span className="font-mono">shipping_weight_g</span>) before saving an active product. Without it every order uses a Rs.99 placeholder.
                                                </>
                                            ) : (
                                                <>
                                                    <strong>Heads up:</strong> this active product has no weight set. Every order will use the Rs.99 placeholder shipping. Add a weight or save as draft.
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {!dimsSet && (
                                        <div
                                            data-testid="pf-shipping-warning-dims"
                                            className="text-xs px-2.5 py-2 rounded-sm border text-amber-400 bg-amber-500/10 border-amber-500/30"
                                        >
                                            No package dimensions set. Couriers may rate-charge on volumetric weight once Shiprocket goes live.
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                        <Field label="Weight (grams) *">
                            <input type="number" min="1" value={form.weight_grams} onChange={(e) => set("weight_grams", e.target.value)} className="ost-input" placeholder="e.g. 500" data-testid="pf-weight" />
                        </Field>
                        <Field label="Shipping type">
                            <select value={form.shipping_type} onChange={(e) => set("shipping_type", e.target.value)} className="ost-input" data-testid="pf-shipping-type">
                                <option value="standard">Standard</option>
                                <option value="rod">Rod / Oversize</option>
                                <option value="heavy">Heavy / Bulky</option>
                            </select>
                        </Field>
                        <div className="grid grid-cols-3 gap-2">
                            <Field label={`Length (cm)${form.shipping_type === "rod" ? " *" : ""}`}>
                                <input type="number" min="1" value={form.length_cm} onChange={(e) => set("length_cm", e.target.value)} className="ost-input" data-testid="pf-length" />
                            </Field>
                            <Field label={`Width (cm)${form.shipping_type === "rod" ? " *" : ""}`}>
                                <input type="number" min="1" value={form.width_cm} onChange={(e) => set("width_cm", e.target.value)} className="ost-input" data-testid="pf-width" />
                            </Field>
                            <Field label={`Height (cm)${form.shipping_type === "rod" ? " *" : ""}`}>
                                <input type="number" min="1" value={form.height_cm} onChange={(e) => set("height_cm", e.target.value)} className="ost-input" data-testid="pf-height" />
                            </Field>
                        </div>
                        <Toggle
                            label="Eligible for free shipping"
                            value={form.free_shipping_eligible}
                            onChange={(v) => set("free_shipping_eligible", v)}
                            testid="pf-free-shipping"
                        />
                        <p className="text-[11px] text-white/45 leading-relaxed">
                            Shipping is calculated server-side from product weight + dimensions.
                            Rod/oversize products always force oversize logistics in mixed carts.
                        </p>
                    </Card>
                    <Card title="SEO">
                        <Field label="Meta title">
                            <input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} className="ost-input" data-testid="pf-seo-title" />
                        </Field>
                        <Field label="Meta description">
                            <textarea value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} className="ost-input min-h-[80px]" data-testid="pf-seo-desc" />
                        </Field>
                    </Card>
                </div>
            </div>
            <style>{`
                .ost-input {
                    width: 100%;
                    background: #0A0A0B;
                    border: 1px solid #27272A;
                    border-radius: 4px;
                    padding: 10px 12px;
                    color: white;
                    outline: none;
                    transition: border-color 150ms ease;
                }
                .ost-input:focus { border-color: #00BCE5; }
            `}</style>
        </form>
    );
}

function Card({ title, children }) {
    return (
        <section className="bg-[#18181B] border border-[#27272A] rounded-sm">
            <div className="px-4 py-3 border-b border-[#27272A] font-display uppercase font-black tracking-tight">{title}</div>
            <div className="p-4 space-y-4">{children}</div>
        </section>
    );
}
function Field({ label, children }) {
    return (
        <div>
            <label className="block text-[11px] uppercase tracking-widest font-display font-bold text-white/55 mb-1.5">
                {label}
            </label>
            {children}
        </div>
    );
}
function Toggle({ label, value, onChange, testid }) {
    return (
        <label className="flex items-center justify-between py-1 cursor-pointer select-none" data-testid={testid}>
            <span className="text-sm text-white/85">{label}</span>
            <button
                type="button"
                onClick={() => onChange(!value)}
                className={`w-10 h-5 rounded-full relative transition-colors ${value ? "bg-[#00BCE5]" : "bg-[#27272A]"}`}
            >
                <span
                    className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                        value ? "translate-x-5" : ""
                    }`}
                />
            </button>
        </label>
    );
}
